/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type KeyboardEvent, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import type { PlayerWithPlugins } from 'video.js';

import { duration as formatDuration } from 'js/utils/DateTimeUtils';

import { useLearningEffortFirstPlayContext } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import type { LearningEffortFirstPlayContextType } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import IVQMarker from 'bundles/video-player/components/progressBar/IVQMarker';
import ProgressBarTimeBadge from 'bundles/video-player/components/progressBar/ProgressBarTimeBadge';
import { NOT_SET } from 'bundles/video-player/constants';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import {
  formatFraction,
  fractionToWholeNumber,
  getIsScrubbingAllowed,
  getShouldShowVideoSkippingDisabledNotification,
} from 'bundles/video-player/utils/videoProgressBarUtils';
import type { InVideoQuestion } from 'bundles/video-quiz/index';

import _t from 'i18n!nls/video-player';

const styles = {
  progressBar: css`
    height: 5px;
    width: calc(100% - 10px);
    position: absolute;
    top: 0;
    left: 5px;
    transform: translate(0, -100%);
    z-index: 999;

    @media (forced-colors: active) {
      outline: 2px solid WindowText;

      .base-track {
        background-color: Window;
      }

      .hitbox {
        background-color: transparent;
      }

      .progress-track {
        background-color: Highlight;
      }

      .learner-max-video-progress-overlay {
        background-color: WindowText;
      }

      .progress-handle {
        background-color: Highlight;
      }
    }

    &:hover {
      cursor: pointer;
    }

    &:focus,
    &:active {
      outline: 2px solid #82a8e1;
    }
  `,
  modernProgressBar: css`
    width: calc(100%);
    left: 0;
  `,
  baseTrack: css`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #a9a9a9;
    transform: translate(0, -50%) scaleY(0.6);
    transition: transform 0.1s cubic-bezier(0.4, 0, 1, 1);
  `,
  modernBaseTrack: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
  hovering: css`
    transform: scale(1) translate(0, -50%);
  `,
  hitbox: css`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / 0%);
    transform: translate(0, -50%);
  `,
  hitboxHovering: css`
    height: 500%;
  `,
  progressTrack: (widthAsPercent: string) => css`
    position: absolute;
    height: 100%;
    background-color: #3cb371;
    z-index: 2;
    width: ${widthAsPercent};
  `,
  modernProgressTrack: css`
    background-color: var(--cds-color-blue-500);
  `,
  maxVideoProgressOverlay: (widthAsPercent: string) => css`
    background: var(--cds-color-neutral-primary-invert);
    height: 100%;
    position: relative;
    z-index: 1;
    width: ${widthAsPercent};
  `,
  progressHandle: (leftOffsetAsPercent: string) => css`
    position: absolute;
    width: 0;
    height: 0;
    border-radius: 50%;
    top: 50%;
    background-color: #3cb371;
    transform: translate(-50%, -50%);
    transition: height 0.1s ease, width 0.1s ease;
    left: ${leftOffsetAsPercent};
  `,
  modernProgressHandle: css`
    background-color: var(--cds-color-blue-500);
  `,
  progressHandleHovering: css`
    width: 13px;
    height: 13px;
  `,
  modernProgressHandleHovering: css`
    width: 16px;
    height: 16px;
  `,
};

type BaseProps = {
  player: PlayerWithPlugins;
  videoQuizQuestions: InVideoQuestion<QuizQuestionPrompt>[] | null;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  trimmedStartTimeSeconds: number;
};

type FirstPlayThroughProps = Pick<
  LearningEffortFirstPlayContextType,
  'shouldDisableSkippingForward' | 'learnerMaxVideoProgressInSeconds' | 'setShowVideoSkippingDisabledNotification'
>;

type Props = BaseProps & FirstPlayThroughProps;

type ActionStateName = 'none' | 'hovering' | 'scrubbing';

type ActionStateOptions = {
  hoverFractionOffsetFromStart?: number | null;
  scrubbingFractionOffsetFromStart?: number | null;
  wasVideoPausedWhenScrubbingStarted?: boolean | null;
};

type ScrubBarMouseState = { actionStateType: ActionStateName } & ActionStateOptions;

export const VideoProgressBarBaseComponent = ({
  player,
  videoQuizQuestions,
  trackVideoPlayerClickEvent,
  trimmedStartTimeSeconds,
  shouldDisableSkippingForward,
  learnerMaxVideoProgressInSeconds,
  setShowVideoSkippingDisabledNotification,
}: Props) => {
  const getCurrentTime = useCallback<() => number>(() => {
    if (!trimmedStartTimeSeconds) {
      return player.currentTime() ?? 0;
    }
    return (player.currentTime() ?? 0) - trimmedStartTimeSeconds;
  }, [player, trimmedStartTimeSeconds]);

  const [currentTime, setCurrentTime] = useState<number | null>(getCurrentTime());
  const [duration, setDuration] = useState<number | null>(player.duration() ?? null);
  const [scrubBarMouseState, setScrubBarMouseState] = useState<ScrubBarMouseState>({ actionStateType: 'none' });
  const baseElement = useRef<HTMLDivElement | null>(null);

  const endScrubbing = useCallback<() => void>(() => {
    const { wasVideoPausedWhenScrubbingStarted, scrubbingFractionOffsetFromStart } = scrubBarMouseState;
    player.trigger('scrubbed');

    if (!wasVideoPausedWhenScrubbingStarted && !player.ended()) {
      player.play();
    }
    setScrubBarMouseState({
      actionStateType: 'hovering',
      hoverFractionOffsetFromStart: scrubbingFractionOffsetFromStart,
    });
  }, [scrubBarMouseState, player]);

  useEffect(() => {
    setCurrentTime(getCurrentTime());
    setDuration(player.duration() ?? null);

    const onTimeChange = () => {
      setCurrentTime(getCurrentTime());
    };

    const onDurationChange = () => {
      setDuration(player.duration() ?? null);
    };

    player.on('timeupdate', onTimeChange);
    player.on('durationchange', onDurationChange);

    const onDocumentMouseUp = () => {
      if (scrubBarMouseState.actionStateType === 'scrubbing') {
        endScrubbing();
      }
    };

    document.addEventListener('mouseup', onDocumentMouseUp);

    return () => {
      player.off('timeupdate', onTimeChange);
      player.off('durationchange', onDurationChange);
      document.removeEventListener('mouseup', onDocumentMouseUp);
    };
  }, [player, getCurrentTime, scrubBarMouseState, endScrubbing]);

  const getProgressBarFractionOffsetFromEvent = (event: MouseEvent<HTMLDivElement>): number | null => {
    if (baseElement.current) {
      const barCoordinates = baseElement.current.getBoundingClientRect();
      const barX = barCoordinates.left;
      const barWidth = barCoordinates.width;
      const eventX = event.clientX;
      const barFractionOffset = (eventX - barX) / barWidth;
      return barFractionOffset;
    }
    return null;
  };

  const skip = (timeToSkip: number) => {
    if (currentTime !== null && currentTime !== undefined && duration !== null && duration !== undefined) {
      const newTime = Math.max(0, Math.min(currentTime + timeToSkip + (trimmedStartTimeSeconds ?? 0), duration));
      player.currentTime(newTime);
    }
  };

  const beginScrubbing = (event: MouseEvent<HTMLDivElement>) => {
    if (!shouldDisableSkippingForward) {
      setScrubBarMouseState({
        actionStateType: 'scrubbing',
        scrubbingFractionOffsetFromStart: getProgressBarFractionOffsetFromEvent(event),
        wasVideoPausedWhenScrubbingStarted: player.paused(),
      });
    }
    player.pause();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      return;
    }

    const skipForward = () => {
      if (!shouldDisableSkippingForward || getCurrentTime() + 5 < learnerMaxVideoProgressInSeconds) {
        skip(5);
      } else {
        setShowVideoSkippingDisabledNotification(true);
      }
    };

    const keyHandlerMap: { [key: string]: () => void } = {
      Up: skipForward,
      ArrowUp: skipForward,
      Right: skipForward,
      ArrowRight: skipForward,
      Down: () => {
        skip(-5);
      },
      ArrowDown: () => {
        skip(-5);
      },
      Left: () => {
        skip(-5);
      },
      ArrowLeft: () => {
        skip(-5);
      },
    };

    if (keyHandlerMap[event.key]) {
      keyHandlerMap[event.key]();
    }
  };

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    beginScrubbing(event);
  };

  const onMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    const { actionStateType, ...remainingData } = scrubBarMouseState;
    let type = actionStateType;
    const offset = getProgressBarFractionOffsetFromEvent(event);
    const additionalData: ActionStateOptions = remainingData;

    if (type === 'scrubbing') {
      additionalData.scrubbingFractionOffsetFromStart = offset;
    } else {
      type = 'hovering';
      additionalData.hoverFractionOffsetFromStart = offset;
    }

    setScrubBarMouseState({ actionStateType: type, ...additionalData });
  };

  const onMouseLeave = () => {
    if (scrubBarMouseState.actionStateType === 'scrubbing') {
      const { scrubbingFractionOffsetFromStart } = scrubBarMouseState;
      if (duration && scrubbingFractionOffsetFromStart) {
        const currentTimeForPlayer = duration * scrubbingFractionOffsetFromStart + (trimmedStartTimeSeconds ?? 0);
        player.currentTime(currentTimeForPlayer);
        const currentTimeForDisplay = duration * scrubbingFractionOffsetFromStart;
        setCurrentTime(currentTimeForDisplay);
      }
    } else {
      setScrubBarMouseState({ actionStateType: 'none' });
    }
  };

  const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const { actionStateType, ...remainingData } = scrubBarMouseState;
    const offset = getProgressBarFractionOffsetFromEvent(event);

    if (actionStateType === 'hovering') {
      setScrubBarMouseState({ actionStateType, ...remainingData, hoverFractionOffsetFromStart: offset });
    } else if (actionStateType === 'scrubbing') {
      setScrubBarMouseState({ actionStateType, ...remainingData, scrubbingFractionOffsetFromStart: offset });
    }
  };

  const onMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const barFractionOffset = getProgressBarFractionOffsetFromEvent(event);

    if (barFractionOffset !== null && barFractionOffset !== undefined) {
      if (
        duration &&
        getIsScrubbingAllowed(
          barFractionOffset,
          duration,
          shouldDisableSkippingForward,
          learnerMaxVideoProgressInSeconds
        )
      ) {
        const defaultPreviousVideoPosition = trimmedStartTimeSeconds ? 0 : NOT_SET;
        const previousVideoPosition = currentTime ?? defaultPreviousVideoPosition;
        const currentTimeForPlayer = barFractionOffset * duration + (trimmedStartTimeSeconds ?? 0);
        const currentTimeForDisplay = barFractionOffset * duration;

        if (previousVideoPosition != null) trackVideoPlayerClickEvent('video_scrub_bar', { previousVideoPosition });

        player.currentTime(currentTimeForPlayer);
        setCurrentTime(currentTimeForDisplay);
      }

      if (
        getShouldShowVideoSkippingDisabledNotification(
          barFractionOffset,
          duration,
          shouldDisableSkippingForward,
          learnerMaxVideoProgressInSeconds
        )
      ) {
        setShowVideoSkippingDisabledNotification(true);
      }
    }

    endScrubbing();
  };

  if (duration === null || duration === undefined || currentTime === null || currentTime === undefined) {
    return null;
  }

  const { hoverFractionOffsetFromStart, scrubbingFractionOffsetFromStart } = scrubBarMouseState;
  const progressFractionOffset = currentTime / duration;
  const progressPercentOffset = formatFraction(progressFractionOffset);
  const questions = videoQuizQuestions;

  let scrubbingOffset = progressPercentOffset;
  let ariaValueNow = fractionToWholeNumber(progressFractionOffset);

  const timeBadgeOffset =
    scrubBarMouseState.actionStateType === 'hovering' ? hoverFractionOffsetFromStart : scrubbingFractionOffsetFromStart;

  if (scrubBarMouseState.actionStateType === 'scrubbing') {
    if (scrubbingFractionOffsetFromStart !== null && scrubbingFractionOffsetFromStart !== undefined) {
      scrubbingOffset = formatFraction(scrubbingFractionOffsetFromStart);
      ariaValueNow = fractionToWholeNumber(scrubbingFractionOffsetFromStart);
    }
  }

  const isUserHovering =
    scrubBarMouseState.actionStateType === 'hovering' || scrubBarMouseState.actionStateType === 'scrubbing';
  const formattedCurrentTime = formatDuration(progressFractionOffset * duration * 1000, 'mmm:sss');
  const formattedDuration = formatDuration(duration * 1000, 'mmm:sss');
  const ariaValueText = _t('Current time #{formattedCurrentTime}, Duration #{formattedDuration}', {
    formattedCurrentTime,
    formattedDuration,
  });

  const learnerMaxVideoProgressPercent = formatFraction(learnerMaxVideoProgressInSeconds / duration);

  return (
    <div
      tabIndex={0}
      role="slider"
      aria-label={_t('Video Progress')}
      aria-valuemin={0}
      aria-valuemax={100}
      dir="ltr"
      aria-valuenow={ariaValueNow}
      aria-valuetext={ariaValueText}
      className={classNames('rc-VideoProgressBar', 'horizontal-box', 'align-items-absolute-center')}
      ref={baseElement}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      data-testid="video-progress-bar"
      css={[styles.progressBar, styles.modernProgressBar]}
    >
      <div
        css={[styles.baseTrack, isUserHovering && styles.hovering, styles.modernBaseTrack]}
        className={classNames('base-track', { hovering: isUserHovering })}
      >
        {questions &&
          questions.map((question) => (
            <IVQMarker
              key={`ivq-${question.id}`}
              question={question}
              videoDurationSeconds={duration}
              trimmedStartTimeSeconds={trimmedStartTimeSeconds}
            />
          ))}
        <div
          css={[styles.progressTrack(scrubbingOffset), styles.modernProgressTrack]}
          className={classNames('progress-track', { hovering: isUserHovering })}
        />
        {shouldDisableSkippingForward && (
          <div
            css={styles.maxVideoProgressOverlay(learnerMaxVideoProgressPercent)}
            className={classNames('learner-max-video-progress-overlay')}
          />
        )}
      </div>
      <div
        className={classNames('hitbox', { hovering: isUserHovering })}
        css={[styles.hitbox, isUserHovering && styles.hitboxHovering]}
      />
      <div
        css={[
          styles.progressHandle(scrubbingOffset),
          styles.modernProgressHandle,
          isUserHovering && styles.progressHandleHovering,
          isUserHovering && styles.modernProgressHandleHovering,
        ]}
        className={classNames('progress-handle', { hovering: isUserHovering })}
      />
      {isUserHovering && timeBadgeOffset && (
        <ProgressBarTimeBadge time={timeBadgeOffset * duration} videoDuration={duration} />
      )}
    </div>
  );
};

const VideoProgressBarWithChapters = (props: BaseProps) => {
  const { shouldDisableSkippingForward, learnerMaxVideoProgressInSeconds, setShowVideoSkippingDisabledNotification } =
    useLearningEffortFirstPlayContext();
  return (
    <VideoProgressBarBaseComponent
      {...props}
      shouldDisableSkippingForward={shouldDisableSkippingForward}
      learnerMaxVideoProgressInSeconds={learnerMaxVideoProgressInSeconds}
      setShowVideoSkippingDisabledNotification={setShowVideoSkippingDisabledNotification}
    />
  );
};

export default VideoProgressBarWithChapters;
